/**
 * Investigation template form component (Module 24).
 * Provides form for creating and editing investigation templates.
 */

import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  FormControlLabel,
  Switch,
  Alert,
  CircularProgress,
  Divider,
  Grid,
  Card,
  CardContent,
  IconButton,
} from '@mui/material';
import { Save as SaveIcon, ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { TextField } from '@core/components/mui';
import { investigationService } from '../../services/investigationService';
import { useNotificationStore } from '../../store/notificationStore';
import { ROUTES } from '../../utils/routes';
import type {
  CreateInvestigationTemplateRequest,
  UpdateInvestigationTemplateRequest,
  InvestigationTemplateDto,
} from '../../types/investigation';

/**
 * Investigation template form component.
 */
export const InvestigationTemplateForm = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEdit = !!id;
  const { showNotification } = useNotificationStore();

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [stepsJson, setStepsJson] = useState('');
  const [defaultPromptsJson, setDefaultPromptsJson] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [isSystemTemplate, setIsSystemTemplate] = useState(false);

  useEffect(() => {
    if (isEdit && id) {
      loadTemplate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isEdit]);

  const loadTemplate = async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      // No dedicated GET-by-id endpoint; fetch all and pick the requested id.
      const all = await investigationService.getInvestigationTemplates(false);
      const t = all.find((x) => x.id === id);
      if (!t) {
        setError('Template not found');
        return;
      }
      hydrateFromDto(t);
    } catch (err: any) {
      const msg = err.response?.data?.message || err.message || 'Failed to load template';
      setError(msg);
      showNotification(msg, 'error');
    } finally {
      setLoading(false);
    }
  };

  const hydrateFromDto = (t: InvestigationTemplateDto) => {
    setName(t.name);
    setDescription(t.description || '');
    setStepsJson(t.stepsJson || '');
    setDefaultPromptsJson(t.defaultPromptsJson || '');
    setIsActive(t.isActive);
    setIsSystemTemplate(t.isSystemTemplate);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!name.trim()) {
      setError('Name is required');
      return;
    }

    setSaving(true);
    try {
      if (isEdit && id) {
        const request: UpdateInvestigationTemplateRequest = {
          name: name.trim(),
          description: description.trim() || undefined,
          stepsJson: stepsJson.trim() || undefined,
          defaultPromptsJson: defaultPromptsJson.trim() || undefined,
          isActive,
        };
        await investigationService.updateInvestigationTemplate(id, request);
        showNotification('Template updated successfully', 'success');
      } else {
        const request: CreateInvestigationTemplateRequest = {
          name: name.trim(),
          description: description.trim() || undefined,
          stepsJson: stepsJson.trim() || undefined,
          defaultPromptsJson: defaultPromptsJson.trim() || undefined,
          isActive,
        };
        await investigationService.createInvestigationTemplate(request);
        showNotification('Template created successfully', 'success');
      }

      navigate(ROUTES.INVESTIGATION_TEMPLATES);
    } catch (err: any) {
      const msg = err.response?.data?.message || err.message || 'Failed to save template';
      setError(msg);
      showNotification(msg, 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Card>
        <CardContent sx={{ p: 6 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
            <IconButton onClick={() => navigate(ROUTES.INVESTIGATION_TEMPLATES)} sx={{ mr: -1 }}>
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h4" sx={{ fontWeight: 600, flex: 1 }}>
              {isEdit ? 'Edit Template' : 'Create Template'}
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 4 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <Grid container spacing={4}>
              <Grid size={{ xs: 12 }}>
                <Typography variant="h6" fontWeight={600}>
                  Template Details
                </Typography>
                <Divider sx={{ mt: 2 }} />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <TextField fullWidth label="Name" value={name} onChange={(e) => setName(e.target.value)} />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  label="Description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  multiline
                  minRows={3}
                />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <FormControlLabel
                  control={<Switch checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />}
                  label="Active"
                />
                {isSystemTemplate && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    This is a system template. Deletion is blocked by the backend.
                  </Typography>
                )}
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Typography variant="h6" fontWeight={600} sx={{ mt: 2 }}>
                  JSON Payloads
                </Typography>
                <Divider sx={{ mt: 2 }} />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  label="Steps JSON"
                  value={stepsJson}
                  onChange={(e) => setStepsJson(e.target.value)}
                  multiline
                  minRows={6}
                  placeholder='[{"stepName":"Collect logs","description":"...","stepType":0,"order":1}]'
                />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  label="Default Prompts JSON"
                  value={defaultPromptsJson}
                  onChange={(e) => setDefaultPromptsJson(e.target.value)}
                  multiline
                  minRows={6}
                  placeholder='{"DataCollection":"Prompt...","Analysis":"Prompt..."}'
                />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
                  <Button variant="outlined" onClick={() => navigate(ROUTES.INVESTIGATION_TEMPLATES)}>
                    Cancel
                  </Button>
                  <Button type="submit" variant="contained" startIcon={<SaveIcon />} disabled={saving}>
                    {saving ? 'Saving…' : 'Save'}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
};

